import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, ImagePlus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { inventoryApi } from '@/features/inventory/api/inventoryApi';
import { supplyAliasRefusalText } from '@/features/inventory/utils/supplyAliasCopy';
import { useRole } from '@/hooks/use-role';

export type BatchVariant = {
  id: string;
  sku: string;
  isLocked: boolean;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  variants: BatchVariant[];
  onUpdated: () => void | Promise<void>;
  /** Called instead of onUpdated when SOME chunks of a >200-variant submit
   *  persisted before a later chunk failed — refresh data WITHOUT clearing
   *  the caller's selection, so the operator can submit again to finish
   *  (field writes are absolute values, so a retry is idempotent). */
  onPartialFailure?: () => void | Promise<void>;
};

export default function BatchEditVariantsModal({ isOpen, onClose, variants, onUpdated, onPartialFailure }: Props) {
  const { t } = useTranslation('inventory');
  // The Default cost field needs skus.costs_manage on the server; without it the write 403s.
  const { can } = useRole();
  const canWriteCosts = can('skus.costs_manage');
  // Field enable toggles
  const [includeTitle, setIncludeTitle] = useState(false);
  const [includeCategory, setIncludeCategory] = useState(false);
  const [includeColor, setIncludeColor] = useState(false);
  const [includeCost, setIncludeCost] = useState(false);
  const [includeImage, setIncludeImage] = useState(false);
  const [includeHsCode, setIncludeHsCode] = useState(false);
  const [includeWeight, setIncludeWeight] = useState(false);
  const [includePrintTech, setIncludePrintTech] = useState(false);

  // Field values
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [color, setColor] = useState('');
  const [costOverride, setCostOverride] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [hsCode, setHsCode] = useState('');
  const [weightValue, setWeightValue] = useState('');
  const [weightUnit, setWeightUnit] = useState('');
  // BLU-673: 'DTG' | 'DTF' | 'CLEAR' (revert to default). POD variants only —
  // the backend skips PAP rows.
  const [printTech, setPrintTech] = useState<'DTG' | 'DTF' | 'CLEAR'>('DTG');

  // BLU-679: when the org maps the Shopify print_technology metafield, that
  // mirror is AUTHORITATIVE — a manual edit here would silently revert within
  // one sync cycle (~15 min). Disable the field + explain, instead of letting
  // the operator make an invisible no-op. FAIL-OPEN on load/error (blocking
  // edits on a transient read is worse; the sweep re-asserts regardless).
  // Server-side writes stay accepted — this is signalling, not enforcement.
  const mappingsQuery = trpc.shopifyMetafieldMappings.list.useQuery(undefined, {
    staleTime: 300_000,
    enabled: isOpen,
  });
  const printTechMapped =
    mappingsQuery.data?.some((m) => m.purpose === 'print_technology') ?? false;
  // Single derived flag for the four gate sites (checkbox, fields, hasAnyField,
  // anyFieldEnabled) so a future site can't miss the mapped-org guard.
  const printTechActive = includePrintTech && !printTechMapped;

  // Submitted in ≤200-id chunks (the bulkUpdateVariants schema cap, which also
  // keeps the PostgREST `.in()` URL bounded) — a whole-page product selection
  // can exceed 200 variants. Result counts are aggregated across chunks.
  const mutation = trpc.inventory.bulkUpdateVariants.useMutation();
  const [submitting, setSubmitting] = useState(false);
  const BULK_CHUNK_SIZE = 200;

  // Phase 41 (BLU-262): All SKUs are now editable — no locked/unlocked distinction

  const handleClose = () => {
    setIncludeTitle(false);
    setIncludeCategory(false);
    setIncludeColor(false);
    setIncludeCost(false);
    setIncludeImage(false);
    setIncludeHsCode(false);
    setIncludeWeight(false);
    setIncludePrintTech(false);
    setPrintTech('DTG');
    setTitle('');
    setCategory('');
    setColor('');
    setCostOverride('');
    setImageUrl('');
    setHsCode('');
    setWeightValue('');
    setWeightUnit('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const fields: {
      title?: string | null;
      category?: string;
      color?: string | null;
      skuCostOverride?: number | null;
      imageUrl?: string | null;
      hsCode?: string | null;
      weightValue?: number | null;
      weightUnit?: 'GRAMS' | 'KILOGRAMS' | 'OUNCES' | 'POUNDS' | null;
      printTechnology?: 'DTG' | 'DTF' | null;
    } = {};

    if (includeTitle) fields.title = title.trim() || null;
    if (includeCategory) fields.category = category.trim() || undefined;
    if (includeColor) fields.color = color.trim() || null;
    if (includeCost) {
      fields.skuCostOverride = costOverride.trim() === '' ? null : Number(costOverride);
    }
    if (includeImage) fields.imageUrl = imageUrl.trim() || null;
    if (includeHsCode) fields.hsCode = hsCode.trim() || null;
    if (includeWeight) {
      fields.weightValue = weightValue.trim() === '' ? null : Number(weightValue);
      fields.weightUnit = (weightUnit || null) as typeof fields.weightUnit;
    }
    // BLU-679: never submit print technology on a mapped org (belt-and-braces
    // with the disabled control — the metafield mirror is authoritative).
    if (printTechActive) {
      fields.printTechnology = printTech === 'CLEAR' ? null : printTech;
    }

    const hasAnyField = includeTitle || includeCategory || includeColor || includeCost || includeImage || includeHsCode || includeWeight || printTechActive;
    if (!hasAnyField) {
      toast.error('Enable at least one field to update');
      return;
    }

    const allIds = variants.map((v) => v.id);
    setSubmitting(true);
    let attemptedCount = 0;
    try {
      const totals = { updatedCount: 0, costUpdatedCount: 0, printTechnologyUpdatedCount: 0, skippedNotOwned: 0 };
      for (let i = 0; i < allIds.length; i += BULK_CHUNK_SIZE) {
        const chunk = allIds.slice(i, i + BULK_CHUNK_SIZE);
        const result = await mutation.mutateAsync({ ...fields, skuIds: chunk });
        attemptedCount += chunk.length;
        totals.updatedCount += result.updatedCount;
        totals.costUpdatedCount += result.costUpdatedCount;
        totals.printTechnologyUpdatedCount += result.printTechnologyUpdatedCount;
        totals.skippedNotOwned += result.skippedNotOwned;
      }
      const parts: string[] = [];
      if (totals.updatedCount > 0) parts.push(`${totals.updatedCount} variant(s) updated`);
      if (totals.costUpdatedCount > 0) parts.push(`${totals.costUpdatedCount} default cost(s) updated`);
      if (totals.printTechnologyUpdatedCount > 0) parts.push(`${totals.printTechnologyUpdatedCount} print technology change(s) (POD only)`);
      const totalUpdated = totals.updatedCount + totals.costUpdatedCount + totals.printTechnologyUpdatedCount;
      if (totalUpdated > 0) {
        toast.success(parts.join(', '));
      }
      // This modal only ever writes the CALLER org's own catalog (`skus` is
      // Class C — see inventory.service.ts bulkUpdateVariants). A non-zero
      // skippedNotOwned here means some selected ids aren't in this org's
      // catalog at all — a client's SKU (e.g. a stale selection from the
      // /products client-catalog view) or a deleted item — and the update
      // silently no-op'd for those rows. Point at the sanctioned cross-org
      // lever instead of leaving the operator to assume it landed. Reported
      // for ANY field edit, not just print technology.
      if (totals.skippedNotOwned > 0) {
        toast.warning(
          `${totals.skippedNotOwned} selected SKUs are not in this organization's catalog (e.g. a client's SKU or a deleted item) and were not changed. For client SKUs, use the client catalog's per-location print technology override.`,
        );
      }
      handleClose();
      await onUpdated();
    } catch (err) {
      // BLU-1400: a selection holding a supply alias (the 3PL's garment) is
      // refused as one sentence, never the raw marker. Submitting again would
      // be refused again, so that case never says to.
      const refusal = supplyAliasRefusalText(t, err);
      const msg = refusal ?? (err instanceof Error && err.message ? err.message : 'Failed to update variants');
      if (attemptedCount > 0) {
        // Earlier chunks persisted server-side. Refresh the caller's data
        // (without clearing its selection) and keep the modal open so
        // submitting again finishes the remainder.
        toast.error(
          refusal
            ? `Applied ${attemptedCount} of ${allIds.length} variant(s). ${refusal}`
            : `Applied ${attemptedCount} of ${allIds.length} variant(s) before an error: ${msg}. Submit again to finish. Already-updated variants are unaffected.`,
        );
        await (onPartialFailure ?? onUpdated)();
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const anyFieldEnabled = includeTitle || includeCategory || includeColor || includeCost || includeImage || includeHsCode || includeWeight || printTechActive;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Batch Edit {variants.length} Variant{variants.length !== 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Toggle fields to include in the update. Only enabled fields will be changed.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Title */}
            <div className="flex items-start gap-3">
                <Checkbox
                  id="include-title"
                  checked={includeTitle}
                  onCheckedChange={(c) => setIncludeTitle(c === true)}
                  className="mt-2.5"
                />
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="batch-title" className="text-sm font-medium">
                    Title
                  </Label>
                  <Input
                    id="batch-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Variant title"
                    disabled={!includeTitle}
                  />
                </div>
              </div>

            {/* Product Type (category) */}
            <div className="flex items-start gap-3">
                <Checkbox
                  id="include-category"
                  checked={includeCategory}
                  onCheckedChange={(c) => setIncludeCategory(c === true)}
                  className="mt-2.5"
                />
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="batch-category" className="text-sm font-medium">
                    Product Type
                  </Label>
                  <Input
                    id="batch-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Baby Tee"
                    disabled={!includeCategory}
                  />
                </div>
              </div>

            {/* Color */}
            <div className="flex items-start gap-3">
                <Checkbox
                  id="include-color"
                  checked={includeColor}
                  onCheckedChange={(c) => setIncludeColor(c === true)}
                  className="mt-2.5"
                />
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="batch-color" className="text-sm font-medium">
                    Color
                  </Label>
                  <Input
                    id="batch-color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="e.g. Blue"
                    disabled={!includeColor}
                  />
                </div>
              </div>

            {/* Default cost */}
            {canWriteCosts && (
              <div className="flex items-start gap-3">
                <Checkbox
                  id="include-cost"
                  checked={includeCost}
                  onCheckedChange={(c) => setIncludeCost(c === true)}
                  className="mt-2.5"
                />
                <div className="flex-1 space-y-1.5">
                  <Label htmlFor="batch-cost" className="text-sm font-medium">
                    {t('batchEdit.costOverrideLabel')}
                  </Label>
                  <Input
                    id="batch-cost"
                    type="number"
                    min="0"
                    step="0.01"
                    value={costOverride}
                    onChange={(e) => setCostOverride(e.target.value)}
                    placeholder={t('batchEdit.costOverridePlaceholder')}
                    disabled={!includeCost}
                  />
                  {includeCost && costOverride.trim() === '' && (
                    <p className="text-xs text-muted-foreground">
                      {t('batchEdit.costOverrideHint')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* HS Code */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="include-hs-code"
                checked={includeHsCode}
                onCheckedChange={(c) => setIncludeHsCode(c === true)}
                className="mt-2.5"
              />
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="batch-hs-code" className="text-sm font-medium">
                  HS Code
                </Label>
                <Input
                  id="batch-hs-code"
                  value={hsCode}
                  onChange={(e) => setHsCode(e.target.value)}
                  placeholder="e.g. 6109.10"
                  maxLength={20}
                  disabled={!includeHsCode}
                />
                {includeHsCode && hsCode.trim() === '' && (
                  <p className="text-xs text-muted-foreground">
                    Leaving empty will clear the HS code from all selected variants.
                  </p>
                )}
              </div>
            </div>

            {/* Weight */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="include-weight"
                checked={includeWeight}
                onCheckedChange={(c) => setIncludeWeight(c === true)}
                className="mt-2.5"
              />
              <div className="flex-1 space-y-1.5">
                <Label className="text-sm font-medium">Weight</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    id="batch-weight-value"
                    type="number"
                    min="0"
                    step="0.01"
                    value={weightValue}
                    onChange={(e) => setWeightValue(e.target.value)}
                    placeholder="e.g. 150"
                    disabled={!includeWeight}
                  />
                  <select
                    value={weightUnit}
                    onChange={(e) => setWeightUnit(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!includeWeight}
                  >
                    <option value="">Select unit</option>
                    <option value="GRAMS">Grams</option>
                    <option value="KILOGRAMS">Kilograms</option>
                    <option value="OUNCES">Ounces</option>
                    <option value="POUNDS">Pounds</option>
                  </select>
                </div>
                {includeWeight && weightValue.trim() === '' && (
                  <p className="text-xs text-muted-foreground">
                    Leaving empty will clear the weight from all selected variants.
                  </p>
                )}
              </div>
            </div>

            {/* Print technology (BLU-673; BLU-679 mapped-org guard) */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="include-print-tech"
                checked={printTechActive}
                onCheckedChange={(c) => setIncludePrintTech(c === true)}
                aria-label="Include print technology in batch update"
                className="mt-2.5"
                disabled={printTechMapped}
              />
              <div className="flex-1 space-y-1.5">
                <Label htmlFor="batch-print-tech" className="text-sm font-medium">
                  Print Technology
                </Label>
                <select
                  id="batch-print-tech"
                  value={printTech}
                  onChange={(e) => setPrintTech(e.target.value as 'DTG' | 'DTF' | 'CLEAR')}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  disabled={!includePrintTech || printTechMapped}
                >
                  <option value="DTG">DTG</option>
                  <option value="DTF">DTF</option>
                  <option value="CLEAR">Clear (revert to default)</option>
                </select>
                {printTechMapped ? (
                  <p className="text-xs text-muted-foreground">
                    Managed by the Shopify Print Technology metafield for this organisation.
                    Edit the metafield in Shopify; the sync applies it within about 15 minutes.
                  </p>
                ) : (
                  includePrintTech && (
                    <p className="text-xs text-muted-foreground">
                      {printTech === 'CLEAR'
                        ? 'Clearing reverts to the location/routing default.'
                        : 'Applies to POD variants only; PAP variants are skipped. Affects future batches only.'}
                    </p>
                  )
                )}
              </div>
            </div>

            {/* Thumbnail */}
            <div className="flex items-start gap-3">
                <Checkbox
                  id="include-image"
                  checked={includeImage}
                  onCheckedChange={(c) => setIncludeImage(c === true)}
                  className="mt-2.5"
                />
                <div className="flex-1 space-y-1.5">
                  <Label className="text-sm font-medium">Thumbnail</Label>
                  {includeImage ? (
                    imageUrl ? (
                      <div className="relative w-16 h-16">
                        <img
                          src={imageUrl}
                          alt="Preview"
                          className="w-16 h-16 rounded-md object-cover border"
                        />
                        <button
                          type="button"
                          className="absolute -top-2 -right-2 rounded-full bg-destructive text-destructive-foreground h-5 w-5 flex items-center justify-center"
                          onClick={() => setImageUrl('')}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <label className="flex flex-col items-center justify-center w-full h-20 border-2 border-dashed rounded-md cursor-pointer hover:bg-muted/50 transition-colors">
                        {imageUploading ? (
                          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                        ) : (
                          <>
                            <ImagePlus className="h-5 w-5 text-muted-foreground mb-1" />
                            <span className="text-xs text-muted-foreground">Click to upload</span>
                          </>
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={imageUploading}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              setImageUploading(true);
                              const url = await inventoryApi.uploadProductImage(file, 'batch-edit');
                              setImageUrl(url);
                            } catch {
                              toast.error('Failed to upload image');
                            } finally {
                              setImageUploading(false);
                            }
                          }}
                        />
                      </label>
                    )
                  ) : (
                    <div className="h-10 flex items-center text-sm text-muted-foreground">
                      Enable to set a shared image
                    </div>
                  )}
                </div>
              </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!anyFieldEnabled || submitting || imageUploading}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating...
                </>
              ) : (
                `Update ${variants.length} Variant${variants.length !== 1 ? 's' : ''}`
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
